import { useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  TrendingUp, CheckCircle, Clock, AlertTriangle,
  Building2, Users, Target, Wrench, FileText, CalendarDays,
  ArrowRight, Activity, DollarSign, BarChart3, ListTodo,
  Plus, Send, Phone, Mail, MessageSquare, StickyNote,
  Trophy, XCircle, Briefcase, Loader2,
} from "lucide-react"
import {
  AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
} from "recharts"
import { useAuthStore } from "@/stores/auth-store"
import {
  useDashboardStats,
  useDashboardFunnel,
  useDashboardActivities,
  useDashboardRevenueTrend,
  useQuickCreateTask,
} from "@/hooks/use-dashboard"
import type { ActivityFeedItem, FunnelPhase } from "@/hooks/use-dashboard"
import { Skeleton } from "@/components/ui/skeleton"
import { ApiErrorState } from "@/components/api-error-state"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"

// ── Formatters ──

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

function formatCompactCurrency(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)} mln PLN`
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(0)} tys. PLN`
  }
  return formatCurrency(value)
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("pl-PL").format(value)
}

function timeAgo(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60_000)
  if (diffMin < 1) return "teraz"
  if (diffMin < 60) return `${diffMin} min temu`
  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) return `${diffHours} godz. temu`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays === 1) return "wczoraj"
  if (diffDays < 7) return `${diffDays} dni temu`
  return date.toLocaleDateString("pl-PL", { day: "numeric", month: "short" })
}

// ── Main Page ──

export function DashboardPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const { data: statsData, isLoading: statsLoading, isError: statsError, error: statsErr, refetch: refetchStats } = useDashboardStats()
  const { data: funnelData, isLoading: funnelLoading } = useDashboardFunnel()
  const { data: activitiesData, isLoading: activitiesLoading } = useDashboardActivities()
  const { data: trendData, isLoading: trendLoading } = useDashboardRevenueTrend()

  const stats = statsData?.data
  const funnelTypes = funnelData?.data ?? []
  const activities = activitiesData?.data ?? []
  const revenueTrend = trendData?.data ?? []

  const greeting = getGreeting()

  return (
    <div className="mx-auto max-w-[1400px] p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          {greeting}, {user?.first_name ?? ""}
        </h1>
        <p className="mt-1 text-xs text-muted-foreground">
          Oto podsumowanie Twojej aktywności
        </p>
      </div>

      {statsLoading ? (
        <DashboardSkeleton />
      ) : statsError ? (
        <ApiErrorState error={statsErr} onRetry={() => refetchStats()} />
      ) : !stats ? (
        <DashboardSkeleton />
      ) : (
        <div className="space-y-5">
          {/* Row 1: Key Metrics */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <MetricCard
              icon={DollarSign}
              label="Pipeline"
              value={formatCurrency(stats.deals.open_value)}
              subtitle={`${stats.deals.open_count} otwartych`}
              onClick={() => navigate("/deals")}
            />
            <MetricCard
              icon={TrendingUp}
              label="Wygrane (msc)"
              value={formatCurrency(stats.deals.won_value_this_month)}
              subtitle={`${stats.deals.won_this_month} transakcji`}
              onClick={() => navigate("/deals")}
            />
            <MetricCard
              icon={ListTodo}
              label="Zadania"
              value={String(stats.tasks.due_today)}
              subtitle={`na dzis · ${stats.tasks.overdue} zaległych`}
              alert={stats.tasks.overdue > 0}
              onClick={() => navigate("/tasks")}
            />
            <MetricCard
              icon={FileText}
              label="Faktury przetermin."
              value={formatCurrency(stats.invoices.overdue_value)}
              subtitle={`${stats.invoices.overdue_count} faktur`}
              alert={stats.invoices.overdue_count > 0}
              onClick={() => navigate("/invoices")}
            />
          </div>

          {/* Row 2: Sales Funnel + Activity Feed */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <SalesFunnelWidget
                funnelTypes={funnelTypes}
                isLoading={funnelLoading}
                onPhaseClick={(phaseId) => navigate(`/deals?phase_id=${phaseId}`)}
                onMoreClick={() => navigate("/deals")}
              />
            </div>
            <div className="lg:col-span-2">
              <ActivityFeedWidget
                activities={activities}
                isLoading={activitiesLoading}
                onNavigate={navigate}
              />
            </div>
          </div>

          {/* Row 3: Revenue Trend + Quick Tasks */}
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <RevenueTrendWidget
              data={revenueTrend}
              isLoading={trendLoading}
            />
            <QuickTasksWidget
              stats={stats}
              onNavigate={navigate}
            />
          </div>

          {/* Row 4: Contacts + Service + Goals */}
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {/* Contacts & Companies */}
            <WidgetCard title="Kontakty" icon={Users} onMore={() => navigate("/contacts")}>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Kontrahenci</span>
                  </div>
                  <div className="text-right">
                    <span className="tabular-nums text-sm font-semibold text-foreground">
                      {formatNumber(stats.companies.total)}
                    </span>
                    {stats.companies.new_this_month > 0 && (
                      <span className="ml-1.5 text-xs text-green-600">+{stats.companies.new_this_month}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Osoby kontaktowe</span>
                  </div>
                  <div className="text-right">
                    <span className="tabular-nums text-sm font-semibold text-foreground">
                      {formatNumber(stats.contacts.total)}
                    </span>
                    {stats.contacts.new_this_month > 0 && (
                      <span className="ml-1.5 text-xs text-green-600">+{stats.contacts.new_this_month}</span>
                    )}
                  </div>
                </div>
              </div>
            </WidgetCard>

            {/* Service Tickets */}
            <WidgetCard title="Serwis" icon={Wrench} onMore={() => navigate("/service")}>
              <div className="grid grid-cols-3 gap-2 text-center">
                <MiniStat label="Otwarte" value={stats.service_tickets.open} color="#3b82f6" />
                <MiniStat label="W realizacji" value={stats.service_tickets.in_progress} color="#f59e0b" />
                <MiniStat label="Zamkniete (tydz.)" value={stats.service_tickets.resolved_this_week} color="#10b981" />
              </div>
            </WidgetCard>

            {/* Goals */}
            <WidgetCard title="Cele" icon={Target} onMore={() => navigate("/goals")}>
              <div className="grid grid-cols-2 gap-2">
                <MiniStat label="Aktywne" value={stats.goals.active} color="var(--primary)" />
                <MiniStat label="Na dobrej drodze" value={stats.goals.on_track} color="#10b981" />
                <MiniStat label="Zagrozone" value={stats.goals.at_risk} color="#f59e0b" />
                <MiniStat label="Osiagniete" value={stats.goals.completed} color="#10b981" />
              </div>
            </WidgetCard>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Sales Funnel Widget ──

function SalesFunnelWidget({
  funnelTypes,
  isLoading,
  onPhaseClick,
  onMoreClick,
}: {
  funnelTypes: { deal_type_id: string; deal_type_name: string; phases: FunnelPhase[]; won: { count: number; value: number }; lost: { count: number; value: number } }[]
  isLoading: boolean
  onPhaseClick: (phaseId: string) => void
  onMoreClick: () => void
}) {
  if (isLoading) {
    return (
      <div className="surface-card p-5">
        <div className="mb-4 flex items-center gap-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 rounded-lg" style={{ width: `${100 - i * 12}%` }} />
          ))}
        </div>
      </div>
    )
  }

  const funnel = funnelTypes[0] // Show first deal type by default
  if (!funnel || funnel.phases.length === 0) {
    return (
      <WidgetCard title="Lejek sprzedazy" icon={BarChart3} onMore={onMoreClick}>
        <p className="py-8 text-center text-sm text-muted-foreground">
          Brak danych do wyswietlenia
        </p>
      </WidgetCard>
    )
  }

  const maxValue = Math.max(...funnel.phases.map((p) => p.deal_count), 1)
  const totalDeals = funnel.phases.reduce((s, p) => s + p.deal_count, 0)
  const totalValue = funnel.phases.reduce((s, p) => s + p.total_value, 0)

  return (
    <WidgetCard title="Lejek sprzedazy" icon={BarChart3} onMore={onMoreClick}>
      {/* Summary line */}
      <div className="mb-4 flex items-center gap-4 text-xs text-muted-foreground">
        <span>{totalDeals} transakcji w pipeline</span>
        <span className="h-3 w-px bg-border" />
        <span>{formatCompactCurrency(totalValue)}</span>
        {funnel.won.count > 0 && (
          <>
            <span className="h-3 w-px bg-border" />
            <span className="text-green-600">{funnel.won.count} wygranych</span>
          </>
        )}
      </div>

      {/* Funnel bars */}
      <div className="space-y-2">
        {funnel.phases.map((phase) => {
          const widthPct = Math.max((phase.deal_count / maxValue) * 100, 8)
          return (
            <button
              key={phase.id}
              className="group flex w-full items-center gap-3 rounded-lg px-1 py-1.5 text-left transition-colors hover:bg-muted/40"
              onClick={() => onPhaseClick(phase.id)}
            >
              {/* Phase bar */}
              <div className="relative min-w-0 flex-1">
                <div
                  className="flex h-9 items-center rounded-md px-3 transition-all"
                  style={{
                    width: `${widthPct}%`,
                    backgroundColor: phase.color + "20",
                    borderLeft: `3px solid ${phase.color}`,
                  }}
                >
                  <span className="truncate text-sm font-medium text-foreground">
                    {phase.name}
                  </span>
                </div>
              </div>
              {/* Stats */}
              <div className="flex shrink-0 items-center gap-3 pr-1">
                <span className="tabular-nums text-sm font-semibold text-foreground">
                  {phase.deal_count}
                </span>
                <span className="tabular-nums text-xs text-muted-foreground min-w-24 text-right">
                  {formatCompactCurrency(phase.total_value)}
                </span>
              </div>
            </button>
          )
        })}
      </div>
    </WidgetCard>
  )
}

// ── Activity Feed Widget ──

const ACTIVITY_ICON_MAP: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  deal_created: Briefcase,
  deal_won: Trophy,
  deal_lost: XCircle,
  phase_changed: ArrowRight,
  task_completed: CheckCircle,
  note: StickyNote,
  phone: Phone,
  email: Mail,
  meeting: Users,
  sms: MessageSquare,
  ticket_created: Wrench,
  ticket_resolved: CheckCircle,
  ticket_phase_changed: ArrowRight,
  invoice_issued: FileText,
  invoice_paid: DollarSign,
  quote_accepted: CheckCircle,
  quote_rejected: XCircle,
  system: Activity,
  field_changed: Activity,
}

const ACTIVITY_COLOR_MAP: Record<string, string> = {
  deal_created: "#3b82f6",
  deal_won: "#10b981",
  deal_lost: "#ef4444",
  phase_changed: "#8b5cf6",
  task_completed: "#10b981",
  note: "#f59e0b",
  phone: "#06b6d4",
  email: "#6366f1",
  meeting: "#8b5cf6",
  sms: "#14b8a6",
  ticket_created: "#f59e0b",
  ticket_resolved: "#10b981",
  ticket_phase_changed: "#8b5cf6",
  invoice_issued: "#3b82f6",
  invoice_paid: "#10b981",
  quote_accepted: "#10b981",
  quote_rejected: "#ef4444",
}

const ACTIVITY_LABELS: Record<string, string> = {
  deal_created: "Nowa transakcja",
  deal_won: "Transakcja wygrana",
  deal_lost: "Transakcja przegrana",
  phase_changed: "Zmiana etapu",
  task_completed: "Zadanie ukonczone",
  note: "Notatka",
  phone: "Rozmowa telefoniczna",
  email: "E-mail",
  meeting: "Spotkanie",
  sms: "SMS",
  ticket_created: "Nowe zgloszenie",
  ticket_resolved: "Zgloszenie rozwiazane",
  ticket_phase_changed: "Zmiana etapu zgloszenia",
  invoice_issued: "Faktura wystawiona",
  invoice_paid: "Faktura oplacona",
  quote_accepted: "Oferta zaakceptowana",
  quote_rejected: "Oferta odrzucona",
  field_changed: "Zmiana pola",
  system: "Systemowe",
}

function ActivityFeedWidget({
  activities,
  isLoading,
  onNavigate,
}: {
  activities: ActivityFeedItem[]
  isLoading: boolean
  onNavigate: (path: string) => void
}) {
  if (isLoading) {
    return (
      <div className="surface-card p-5">
        <div className="mb-4 flex items-center gap-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <WidgetCard title="Ostatnie aktywnosci" icon={Activity} onMore={() => onNavigate("/activities")}>
      {activities.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Brak ostatnich aktywnosci
        </p>
      ) : (
        <div className="space-y-1">
          {activities.map((act) => {
            const IconComponent = ACTIVITY_ICON_MAP[act.activity_type] ?? Activity
            const color = ACTIVITY_COLOR_MAP[act.activity_type] ?? "var(--muted-foreground)"
            const label = ACTIVITY_LABELS[act.activity_type] ?? act.activity_type
            const userName = act.user
              ? `${act.user.first_name} ${act.user.last_name}`
              : "System"
            const initials = act.user
              ? `${act.user.first_name[0]}${act.user.last_name[0]}`
              : "S"

            return (
              <div
                key={act.id}
                className="flex items-start gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-muted/30"
              >
                {/* Icon */}
                <div
                  className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: color + "18" }}
                >
                  <IconComponent className="h-3.5 w-3.5" style={{ color }} />
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-foreground">{label}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Avatar size="sm" className="h-4 w-4">
                      <AvatarFallback className="text-[8px]">{initials}</AvatarFallback>
                    </Avatar>
                    <span className="truncate">{userName}</span>
                    {act.subject && (
                      <>
                        <span>-</span>
                        <span className="truncate">{act.subject}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Time */}
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {timeAgo(act.activity_date)}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </WidgetCard>
  )
}

// ── Revenue Trend Widget ──

function RevenueTrendWidget({
  data,
  isLoading,
}: {
  data: { month: string; month_label: string; revenue: number; deal_count: number }[]
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <div className="surface-card p-5">
        <div className="mb-4 flex items-center gap-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-36" />
        </div>
        <Skeleton className="h-[200px] rounded-lg" />
      </div>
    )
  }

  const hasData = data.some((d) => d.revenue > 0)

  // Calculate trend
  let trendDirection: "up" | "down" | "flat" = "flat"
  if (data.length >= 2) {
    const last = data[data.length - 1].revenue
    const prev = data[data.length - 2].revenue
    if (last > prev) trendDirection = "up"
    else if (last < prev) trendDirection = "down"
  }

  const totalRevenue = data.reduce((s, d) => s + d.revenue, 0)

  return (
    <WidgetCard title="Trend przychodow" icon={TrendingUp}>
      {/* Summary */}
      <div className="mb-3 flex items-center gap-4">
        <div>
          <p className="tabular-nums text-lg font-bold text-foreground">
            {formatCurrency(totalRevenue)}
          </p>
          <p className="text-[11px] text-muted-foreground">ostatnie 6 msc</p>
        </div>
        {trendDirection !== "flat" && (
          <div className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
            trendDirection === "up" ? "bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400" : "bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400"
          }`}>
            <TrendingUp className={`h-3 w-3 ${trendDirection === "down" ? "rotate-180" : ""}`} />
            {trendDirection === "up" ? "Wzrost" : "Spadek"}
          </div>
        )}
      </div>

      {/* Chart */}
      {hasData ? (
        <div className="h-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="var(--primary)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis
                dataKey="month_label"
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) => formatCompactCurrency(v)}
                width={70}
              />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: "var(--bg-surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "8px",
                  fontSize: "12px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
                formatter={(value) => [formatCurrency(Number(value ?? 0)), "Przychod"]}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="var(--primary)"
                strokeWidth={2}
                fill="url(#revenueGradient)"
                dot={{ r: 3, fill: "var(--primary)", strokeWidth: 0 }}
                activeDot={{ r: 5, fill: "var(--primary)", strokeWidth: 2, stroke: "var(--bg-surface)" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex h-[180px] items-center justify-center text-sm text-muted-foreground">
          Brak danych przychodow
        </div>
      )}
    </WidgetCard>
  )
}

// ── Quick Tasks Widget ──

function QuickTasksWidget({
  stats,
  onNavigate,
}: {
  stats: {
    tasks: { overdue: number; due_today: number; upcoming_7d: number; completed_this_week: number }
  }
  onNavigate: (path: string) => void
}) {
  const [taskTitle, setTaskTitle] = useState("")
  const createTask = useQuickCreateTask()

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const title = taskTitle.trim()
      if (!title) return
      try {
        await createTask.mutateAsync(title)
        setTaskTitle("")
        toast.success("Zadanie utworzone")
      } catch {
        toast.error("Nie udalo sie utworzyc zadania")
      }
    },
    [taskTitle, createTask],
  )

  return (
    <WidgetCard title="Zadania" icon={CheckCircle} onMore={() => onNavigate("/tasks")}>
      {/* Quick task input */}
      <form onSubmit={handleSubmit} className="mb-4 flex items-center gap-2">
        <div className="relative flex-1">
          <Plus className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={taskTitle}
            onChange={(e) => setTaskTitle(e.target.value)}
            placeholder="Nowe zadanie..."
            className="h-8 pl-8 text-sm"
            disabled={createTask.isPending}
          />
        </div>
        <Button
          type="submit"
          size="sm"
          disabled={!taskTitle.trim() || createTask.isPending}
          className="h-8 gap-1 px-3"
        >
          {createTask.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
        </Button>
      </form>

      {/* Task stats */}
      <div className="space-y-2">
        <TaskRow
          icon={AlertTriangle}
          label="Zalegle"
          count={stats.tasks.overdue}
          color="#ef4444"
          alert={stats.tasks.overdue > 0}
        />
        <TaskRow
          icon={CalendarDays}
          label="Na dzis"
          count={stats.tasks.due_today}
          color="#f59e0b"
        />
        <TaskRow
          icon={Clock}
          label="Nastepne 7 dni"
          count={stats.tasks.upcoming_7d}
          color="var(--primary)"
        />
        <TaskRow
          icon={CheckCircle}
          label="Ukonczone (tydzien)"
          count={stats.tasks.completed_this_week}
          color="#10b981"
        />
      </div>
    </WidgetCard>
  )
}

// ── Helper Components ──

function MetricCard({
  icon: Icon,
  label,
  value,
  subtitle,
  alert,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>
  label: string
  value: string
  subtitle: string
  alert?: boolean
  onClick?: () => void
}) {
  return (
    <div
      className="surface-card-interactive cursor-pointer p-5"
      onClick={onClick}
    >
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">
          {label}
        </span>
      </div>
      <p className="tabular-nums text-2xl font-bold tracking-tight text-foreground">{value}</p>
      <p className={`mt-1 text-xs ${alert ? "font-medium text-red-500" : "text-muted-foreground"}`}>
        {subtitle}
      </p>
    </div>
  )
}

function WidgetCard({
  title,
  icon: Icon,
  onMore,
  children,
}: {
  title: string
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>
  onMore?: () => void
  children: React.ReactNode
}) {
  return (
    <div className="surface-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        </div>
        {onMore && (
          <button
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            onClick={onMore}
          >
            Wiecej <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
      {children}
    </div>
  )
}

function MiniStat({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div>
      <p className="tabular-nums text-xl font-bold text-foreground" style={color ? { color } : undefined}>
        {value}
      </p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{label}</p>
    </div>
  )
}

function TaskRow({
  icon: Icon,
  label,
  count,
  color,
  alert,
}: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>
  label: string
  count: number
  color: string
  alert?: boolean
}) {
  return (
    <div className="flex items-center justify-between rounded-md px-2 py-1.5 transition-colors hover:bg-muted/30">
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5" style={{ color }} />
        <span className={`text-sm ${alert ? "font-medium" : "text-muted-foreground"}`} style={alert ? { color } : undefined}>
          {label}
        </span>
      </div>
      <span className={`tabular-nums text-sm font-semibold ${alert && count > 0 ? "" : "text-foreground"}`} style={alert && count > 0 ? { color } : undefined}>
        {count}
      </span>
    </div>
  )
}

function DashboardSkeleton() {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[120px] rounded-xl" />)}
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        <Skeleton className="h-[320px] rounded-xl lg:col-span-3" />
        <Skeleton className="h-[320px] rounded-xl lg:col-span-2" />
      </div>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Skeleton className="h-[280px] rounded-xl" />
        <Skeleton className="h-[280px] rounded-xl" />
      </div>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-[160px] rounded-xl" />)}
      </div>
    </div>
  )
}

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return "Dzien dobry"
  if (hour < 18) return "Dzien dobry"
  return "Dobry wieczor"
}
